import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { AccountContext } from "./AccountContext";

const DemoContext = createContext({
  isDemoMode:      false,
  demoState:       null,
  loading:         false,
  lastCycleResult: null,
  stepForward:     () => {},
  stepBackward:    () => {},
  seekTo:          () => {},
  setStepMode:     () => {},
  initDemo:        () => {},
  refresh:         () => {},
  clearCycleResult: () => {},
});

export function DemoProvider({ children }) {
  const { selectedAccount, demoAccounts } = useContext(AccountContext);

  const [demoState,       setDemoState]       = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);
  const [lastCycleResult, setLastCycleResult] = useState(null);

  const loadingRef   = useRef(false);
  const autoTimerRef = useRef(null);

  // Keep loadingRef in sync so the auto-advance interval can read it without a stale closure
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  const isDemoMode = !!(
    selectedAccount &&
    selectedAccount !== "All Accounts" &&
    Array.isArray(demoAccounts) &&
    demoAccounts.includes(selectedAccount)
  );

  useEffect(() => {
    if (!isDemoMode) {
      setDemoState(null);
      setError(null);
      setLastCycleResult(null);
    }
  }, [isDemoMode]);

  const fetchState = useCallback(async () => {
    if (!isDemoMode) return;
    try {
      const res = await axios.get("/demo/state");
      setDemoState(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to load demo state");
    }
  }, [isDemoMode]);

  // Fetch on demo-account change and poll every 10s while active
  useEffect(() => {
    if (!isDemoMode) return;
    fetchState();
    const id = setInterval(fetchState, 10000);
    return () => clearInterval(id);
  }, [isDemoMode, fetchState]);

  const _step = useCallback(async (direction) => {
    setLoading(true);
    setLastCycleResult(null);
    try {
      const res = await axios.post("/demo/step", { direction });
      setDemoState(prev => ({ ...prev, demo_clock: res.data.demo_clock }));
      if (res.data.cycle) setLastCycleResult(res.data.cycle);
    } catch (e) {
      setError(e?.response?.data?.error || "Step failed");
    } finally {
      setLoading(false);
    }
  }, []);

  const stepForward  = useCallback(() => _step("forward"),  [_step]);
  const stepBackward = useCallback(() => _step("backward"), [_step]);

  // Auto-advance every 5 minutes when in cycle mode.
  // Skipped when the backend automated replay system is enabled — it advances the clock itself.
  // Timer resets after each completed step (demo_clock changes) so steps stay 5 min apart.
  useEffect(() => {
    clearInterval(autoTimerRef.current);
    autoTimerRef.current = null;

    const isCycleMode = isDemoMode && demoState?.step_mode === 'cycle' && demoState?.demo_clock;
    if (!isCycleMode) return;
    if (demoState?.is_replay_enabled) return; // backend cycle runner handles advancement

    autoTimerRef.current = setInterval(() => {
      if (!loadingRef.current) stepForward();
    }, 5 * 60 * 1000);

    return () => {
      clearInterval(autoTimerRef.current);
      autoTimerRef.current = null;
    };
  }, [isDemoMode, demoState?.step_mode, demoState?.demo_clock, stepForward]);

  const seekTo = useCallback(async (timestamp) => {
    setLoading(true);
    setLastCycleResult(null);
    try {
      const res = await axios.post("/demo/seek", { timestamp });
      setDemoState(prev => ({ ...prev, demo_clock: res.data.demo_clock }));
      if (res.data.cycle) setLastCycleResult(res.data.cycle);
    } catch (e) {
      setError(e?.response?.data?.error || "Seek failed");
    } finally {
      setLoading(false);
    }
  }, []);

  const setStepMode = useCallback(async (mode) => {
    try {
      await axios.post("/demo/step-mode", { mode });
      setDemoState(prev => ({ ...prev, step_mode: mode }));
    } catch (e) {
      setError(e?.response?.data?.error || "Mode switch failed");
    }
  }, []);

  const initDemo = useCallback(async (startDate, stepMode = "cycle") => {
    setLoading(true);
    setLastCycleResult(null);
    try {
      const res = await axios.post("/demo/init", { start_date: startDate, step_mode: stepMode });
      setDemoState({ demo_clock: res.data.demo_clock, cash_balance: res.data.cash_balance, step_mode: res.data.step_mode });
    } catch (e) {
      setError(e?.response?.data?.error || "Init failed");
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearCycleResult = useCallback(() => setLastCycleResult(null), []);

  return (
    <DemoContext.Provider value={{
      isDemoMode,
      demoState,
      loading,
      error,
      lastCycleResult,
      stepForward,
      stepBackward,
      seekTo,
      setStepMode,
      initDemo,
      refresh: fetchState,
      clearCycleResult,
    }}>
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo() {
  return useContext(DemoContext);
}

export default DemoContext;
